import { InfoOutlined, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
  FormControlLabel,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";

import ViewDocumentModal from "../../../../../components/modules/Documents/ViewDocuments/components/ViewDocumentModal";
import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import Colors from "../../../../global/Colors";
import PermissionWrapper from "../../../../global/PermissionWrapper";
import ResponseModal from "../../../../global/ResponseModal";
import Svg from "../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../ui library/HNSColors";
import { useNavigateDetail } from "../../hooks/useNavigateDetail";
import { useViewDocumentsPermissions } from "../../hooks/useViewDocumentsPermissions";
import { truncateFileName } from "../../utils/functions";
import { TRUNCATE_FILE_NAME_MAX_LENGTH, NA } from "../../utils/strings";

import DocumentsTableActions from "./DocumentsTableActions";

export default function DocumentsTableBody({
  documents,
  documentsList,
  setDocumentsList,
  updateSelectedDocs,
  setAnchorEl,
  setSelectedDoc,
  setIsLoading,
  setUploadSuccess,
  setUploadFailed,
  setErrorArray,
  enableThreeDotMenu,
  canAddAssociation,
  onDocumentUpdate,
  isDocumentsPage,
  hasDocuments,
}) {
  const { canExportAllDocuments, canCheckDocuments,
    canViewCheckCheckbox,
    canApproveDocuments,
    canViewApprovalCheckbox, } = useViewDocumentsPermissions();

  const documentRepository = useService("repositories/document-repository");

  const navigateToClientDocument = useNavigateDetail();

  const [viewDocument, setViewDocument] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [errorQueryClientName, setErrorQueryClientName] = useState(false);

  const handleSelect = (id, event) => {
    const isChecked = event.target.checked;
    const updatedDocList = documentsList?.map((doc) =>
      doc?.id === id ? { ...doc, selected: isChecked } : doc
    );
    setDocumentsList(updatedDocList);
    updateSelectedDocs(id, isChecked);
  };

  const updateCheckStatus = async (e, doc) => {
    setIsLoading(true);
    try {
      const body = {
        [e.target.name]: e.target.checked,
      };
      
      await documentRepository.patch(doc?.id, body);
      setUploadSuccess(true);
      setIsLoading(false);
    } catch (error) {
      setUploadFailed(true);
      setIsLoading(false);
      const errArr = ApiRequestErrorHandler(error ?? []);
      setErrorArray(errArr);
    }
  };

  function handleOpenDocument(document) {
    setViewDocument(true);
    setActiveDoc(document);
  }

  function closeDocModal() {
    setViewDocument(false);
    setActiveDoc(null);
  }

  useEffect(() => {
    setDocumentsList(documents?.map((doc) => ({ ...doc, selected: false })));
  }, [documents]);

  return (
    <>
      <TableBody>
        {documentsList?.map((doc) => {
          return (
            <TableRow key={doc?.id} hover>
              {!isDocumentsPage && hasDocuments && (
                <PermissionWrapper permission={canExportAllDocuments}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      sx={{
                        transform: "scale(1.2)",
                        "&.Mui-checked": {
                          color: `${HNSColors.primary[500]}`,
                        },
                      }}
                      color="primary"
                      checked={doc?.selected}
                      onChange={(event) => handleSelect(doc?.id, event)}
                    />
                  </TableCell>
                </PermissionWrapper>
              )}
              {isDocumentsPage && (
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Navigate to Client Documents">
                      <IconButton
                        onClick={() => navigateToClientDocument(doc?.case)}
                        className="py-0 pl-0 pr-0.5"
                      >
                        <OpenInNew
                          sx={{
                            fontSize: "1.5rem",
                            color: HNSColors.neutrals[400],
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Typography>{doc?.clientName ?? NA}</Typography>
                  </Box>
                </TableCell>
              )}
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title="View Document">
                    <div onClick={() => handleOpenDocument(doc)} className="cursor-pointer pt-1">
                      <Svg name="his-file-search" color={HNSColors.primary[500]} />
                    </div>
                  </Tooltip>
                  <Tooltip
                    title={doc?.fileName}
                    disableHoverListener={doc?.fileName.length <= TRUNCATE_FILE_NAME_MAX_LENGTH}
                  >
                    <Typography>{doc?.fileName ? truncateFileName(doc?.fileName) : NA}</Typography>
                  </Tooltip>
                  <Tooltip
                    title={
                      <>
                        <p>Created at: {format(doc?.createdAt, "MM/dd/yyyy")}</p>
                        <p>Updated at: {format(doc?.updatedAt, "MM/dd/yyyy")}</p>
                        <p>Created by: {doc?.createdByName}</p>
                        <p>Updated by: {doc?.updatedByName}</p>
                      </>
                    }
                  >
                    <InfoOutlined fontSize="small" sx={{ color: HNSColors.primary[500] }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>{doc?.documentTypeLabel ?? NA}</TableCell>
              <TableCell>{doc?.sourceLabel ?? NA}</TableCell>
              <TableCell style={{ whiteSpace: "pre-line" }}>
                {doc.relatedProviders.length > 0
                  ? doc.relatedProviders.map((p) => p).join("\n")
                  : NA}
              </TableCell>
              <TableCell>{`${doc?.associatedAppointmentCount} / ${doc?.providerTotalAppointmentCount}`}</TableCell>
              {canViewCheckCheckbox && <TableCell>
                <FormControlLabel
                  name="checked"
                  disabled={!canCheckDocuments}
                  control={
                    <Tooltip title="Set Document Checked">
                      <Checkbox
                        disabled={!canCheckDocuments}
                        checked={doc?.checked}
                        sx={{
                          "&.MuiButtonBase-root.MuiCheckbox-root.Mui-checked.Mui-disabled": {
                            color: `${HNSColors.primary[800]}`,
                          },
                          transform: "scale(1.2)",
                          "&.Mui-checked": {
                            color: `${HNSColors.primary[500]}`,
                          },
                        }}
                      />
                    </Tooltip>
                  }
                  onChange={(e) => updateCheckStatus(e, doc)}
                />
              </TableCell>}
              {canViewApprovalCheckbox && <TableCell>
                <FormControlLabel
                  name="approved"
                  disabled={!canApproveDocuments}
                  control={
                    <Tooltip title="Set Document Approved">
                      <Checkbox
                        disabled={!canApproveDocuments}
                        checked={doc?.approved}
                        sx={{
                          "&.MuiButtonBase-root.MuiCheckbox-root.Mui-checked.Mui-disabled": {
                            color: Colors.gray[400],
                          },
                          transform: "scale(1.2)",
                          "&.Mui-checked": {
                            color: `${HNSColors.primary[500]}`,
                          },
                        }}
                      />
                    </Tooltip>
                  }
                  onChange={(e) => updateCheckStatus(e, doc)}
                />
              </TableCell>}
              <PermissionWrapper permission={canAddAssociation || enableThreeDotMenu}>
                <TableCell align="right">
                  <DocumentsTableActions
                    setAnchorEl={setAnchorEl}
                    setSelectedDoc={setSelectedDoc}
                    document={doc}
                    onDocumentUpdate={onDocumentUpdate}
                    enableThreeDotMenu={enableThreeDotMenu}
                    canAddAssociation={canAddAssociation}
                  />
                </TableCell>
              </PermissionWrapper>
            </TableRow>
          );
        })}
      </TableBody>
      {activeDoc && viewDocument && (
        <ViewDocumentModal
          viewDocument={viewDocument}
          closeDocModal={closeDocModal}
          activeDoc={activeDoc}
        />
      )}
      <ResponseModal
        title="Error fetching Client name(s)"
        isError={true}
        openBool={errorQueryClientName}
        setOpenBool={setErrorQueryClientName}
        errorMessage={null}
      />
    </>
  );
}
